// F-105, F-107 : registre des traitements, notices, conservation et anonymisation, effacements, accès aux pièces.
import { useState } from "react";
import { Link, useParams } from "react-router";
import { del, post, put } from "@core/api";
import { PublicShell } from "@core/layout";
import { date, dateTime } from "@core/format";
import { useGet, useInvalidate } from "@core/hooks";
import { useOrg } from "@core/org";
import { Badge, Button, Card, Empty, ErrorBox, Input, Loading, Modal, Notice, PageHeader, Select, Table, Tabs, Textarea, fieldError, useToast } from "@core/ui";
import { HoldsCard, RightsRequestsTab } from "./conservation";
import { LEGAL_BASIS, useAction } from "./shared";

type Tab = "registre" | "notices" | "demandes" | "conservation" | "effacements" | "acces";

export function PrivacyPage() {
  const [tab, setTab] = useState<Tab>("registre");
  return (
    <div className="page">
      <PageHeader
        eyebrow="Espace"
        title="Données personnelles"
        intro="Ce que vous traitez, pourquoi, pour qui et combien de temps. Les contenus préremplis sont des propositions à faire valider ; ils ne valent pas avis juridique."
      />
      <Tabs
        value={tab}
        onChange={setTab}
        items={[
          { value: "registre", label: "Registre des traitements" },
          { value: "notices", label: "Notices d’information" },
          { value: "demandes", label: "Demandes des personnes" },
          { value: "conservation", label: "Conservation" },
          { value: "effacements", label: "Journal des effacements" },
          { value: "acces", label: "Accès aux pièces" },
        ]}
      />
      {tab === "registre" ? <RegisterTab /> : null}
      {tab === "notices" ? <NoticesTab /> : null}
      {tab === "demandes" ? <RightsRequestsTab /> : null}
      {tab === "conservation" ? <RetentionTab /> : null}
      {tab === "effacements" ? <ErasuresTab /> : null}
      {tab === "acces" ? <AccessTab /> : null}
    </div>
  );
}

type Entry = {
  id: string;
  code: string;
  activity: string;
  purposes: string;
  legal_basis: string;
  legal_basis_note: string;
  data_subjects: string;
  data_categories: string;
  recipients: string;
  processors: string;
  transfers: string;
  retention: string;
  security: string;
  audiences: string[];
  status: string;
  origin: string;
  validated_at: string | null;
  updated_at: string;
};

const EMPTY_ENTRY: Omit<Entry, "id" | "code" | "status" | "origin" | "validated_at" | "updated_at"> = {
  activity: "",
  purposes: "",
  legal_basis: "contrat",
  legal_basis_note: "",
  data_subjects: "",
  data_categories: "",
  recipients: "",
  processors: "",
  transfers: "",
  retention: "",
  security: "",
  audiences: [],
};

const AUDIENCES = [
  { value: "candidat", label: "Candidats" },
  { value: "locataire", label: "Locataires" },
  { value: "bailleur", label: "Bailleur" },
];

function RegisterTab() {
  const org = useOrg();
  const q = useGet<any>(org.api("/privacy/register"));
  const invalidate = useInvalidate();
  const toast = useToast();
  const action = useAction();
  const [editing, setEditing] = useState<Partial<Entry> | null>(null);
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  const items: Entry[] = q.data.items;
  const save = async () => {
    if (!editing) return;
    const body = { ...EMPTY_ENTRY, ...editing };
    const r = await action.run(() => (editing.id ? put(org.api(`/privacy/register/${editing.id}`), body) : post(org.api("/privacy/register"), body)));
    if (r) {
      toast("Traitement enregistré. Il est à valider de nouveau.");
      setEditing(null);
      invalidate(org.api("/privacy/register"));
    }
  };
  const validate = async (e: Entry) => {
    const r = await action.run(() => post(org.api(`/privacy/register/${e.id}/validate`)));
    if (r) {
      toast("Traitement marqué comme validé.");
      invalidate(org.api("/privacy/register"));
    }
  };
  const remove = async (e: Entry) => {
    const r = await action.run(() => del(org.api(`/privacy/register/${e.id}`)));
    if (r) invalidate(org.api("/privacy/register"));
  };
  return (
    <div className="stack">
      <Notice tone="attention">{q.data.note}</Notice>
      <ErrorBox error={action.error && !editing ? action.error : null} />
      {org.can("members") ? (
        <p>
          <Button variant="secondaire" onClick={() => setEditing({ ...EMPTY_ENTRY })}>
            Ajouter un traitement
          </Button>
        </p>
      ) : null}
      {items.map((e) => (
        <Card
          key={e.id}
          title={e.activity}
          actions={
            <>
              {e.status === "valide" ? <Badge tone="succes">Validé le {date(e.validated_at)}</Badge> : <Badge tone="attention">À valider</Badge>}
              {e.origin === "prerempli" ? <Badge>Prérempli</Badge> : e.origin === "modifie" ? <Badge>Modifié</Badge> : <Badge>Ajouté</Badge>}
            </>
          }
        >
          <dl className="kv">
            {[
              ["Finalité", e.purposes],
              ["Base légale", `${LEGAL_BASIS[e.legal_basis] ?? e.legal_basis}${e.legal_basis_note ? ` — ${e.legal_basis_note}` : ""}`],
              ["Personnes concernées", e.data_subjects],
              ["Données", e.data_categories],
              ["Destinataires", e.recipients],
              ["Sous-traitants", e.processors || "—"],
              ["Transferts hors UE", e.transfers || "—"],
              ["Conservation", e.retention],
              ["Sécurité", e.security || "—"],
              ["Notices", e.audiences.map((a) => AUDIENCES.find((x) => x.value === a)?.label ?? a).join(", ") || "—"],
            ].map(([k, v]) => (
              <div key={k}>
                <dt>{k}</dt>
                <dd>{v}</dd>
              </div>
            ))}
          </dl>
          {org.can("members") ? (
            <div className="row">
              <Button variant="secondaire" small onClick={() => setEditing(e)}>
                Modifier
              </Button>
              {org.role === "proprietaire" && e.status !== "valide" ? (
                <Button variant="discret" small busy={action.busy} onClick={() => validate(e)}>
                  Indiquer que ce traitement a été validé
                </Button>
              ) : null}
              {org.role === "proprietaire" && e.origin === "ajoute" ? (
                <Button variant="danger" small busy={action.busy} onClick={() => remove(e)}>
                  Supprimer
                </Button>
              ) : null}
            </div>
          ) : null}
        </Card>
      ))}
      <Modal
        open={!!editing}
        onClose={() => setEditing(null)}
        title={editing?.id ? "Modifier le traitement" : "Nouveau traitement"}
        footer={
          <>
            <Button variant="discret" onClick={() => setEditing(null)}>
              Annuler
            </Button>
            <Button busy={action.busy} onClick={save}>
              Enregistrer
            </Button>
          </>
        }
      >
        {editing ? (
          <div className="stack-sm">
            <ErrorBox error={action.error} />
            <Input label="Traitement" required value={editing.activity ?? ""} error={fieldError(action.error, "activity")} onChange={(ev) => setEditing({ ...editing, activity: ev.target.value })} />
            <Textarea label="Finalité" required value={editing.purposes ?? ""} error={fieldError(action.error, "purposes")} onChange={(ev) => setEditing({ ...editing, purposes: ev.target.value })} />
            <Select
              label="Base légale"
              value={editing.legal_basis ?? "contrat"}
              options={Object.entries(LEGAL_BASIS).map(([value, label]) => ({ value, label }))}
              onChange={(ev) => setEditing({ ...editing, legal_basis: ev.target.value })}
            />
            <Input label="Précision sur la base légale" value={editing.legal_basis_note ?? ""} onChange={(ev) => setEditing({ ...editing, legal_basis_note: ev.target.value })} />
            <Input label="Personnes concernées" required value={editing.data_subjects ?? ""} error={fieldError(action.error, "data_subjects")} onChange={(ev) => setEditing({ ...editing, data_subjects: ev.target.value })} />
            <Textarea label="Catégories de données" required value={editing.data_categories ?? ""} error={fieldError(action.error, "data_categories")} onChange={(ev) => setEditing({ ...editing, data_categories: ev.target.value })} />
            <Input label="Destinataires" required value={editing.recipients ?? ""} error={fieldError(action.error, "recipients")} onChange={(ev) => setEditing({ ...editing, recipients: ev.target.value })} />
            <Input label="Sous-traitants" value={editing.processors ?? ""} onChange={(ev) => setEditing({ ...editing, processors: ev.target.value })} />
            <Input label="Transferts hors Union européenne" value={editing.transfers ?? ""} onChange={(ev) => setEditing({ ...editing, transfers: ev.target.value })} />
            <Input label="Durée de conservation" required value={editing.retention ?? ""} error={fieldError(action.error, "retention")} onChange={(ev) => setEditing({ ...editing, retention: ev.target.value })} />
            <Textarea label="Mesures de sécurité" value={editing.security ?? ""} onChange={(ev) => setEditing({ ...editing, security: ev.target.value })} />
            <fieldset className="stack-sm">
              <legend>Figure dans la notice destinée aux</legend>
              {AUDIENCES.map((a) => (
                <label key={a.value} className="row">
                  <input
                    type="checkbox"
                    checked={(editing.audiences ?? []).includes(a.value)}
                    onChange={(ev) => {
                      const cur = new Set(editing.audiences ?? []);
                      if (ev.target.checked) cur.add(a.value);
                      else cur.delete(a.value);
                      setEditing({ ...editing, audiences: [...cur] });
                    }}
                  />
                  {a.label}
                </label>
              ))}
            </fieldset>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}

export function NoticeView({ notice }: { notice: any }) {
  return (
    <div className="stack-sm">
      <p>{notice.intro}</p>
      {notice.pending_validation ? <Notice tone="attention">Certaines informations sont en cours de validation par le bailleur.</Notice> : null}
      {notice.sections.map((s: any) => (
        <div key={s.title}>
          <h3>{s.title}</h3>
          <p className="small">{s.text}</p>
        </div>
      ))}
      <p className="small">
        <strong>Vos droits.</strong> {notice.rights}
      </p>
      <p className="small muted mono">Version {notice.version}</p>
    </div>
  );
}

function NoticesTab() {
  const org = useOrg();
  const [audience, setAudience] = useState<"candidat" | "locataire">("candidat");
  const q = useGet<any>(org.api(`/privacy/notices/${audience}`));
  const link = `${window.location.origin}/confidentialite/${org.orgId}/${audience}`;
  return (
    <div className="stack">
      <Tabs
        value={audience}
        onChange={setAudience}
        items={[
          { value: "candidat", label: "Candidats" },
          { value: "locataire", label: "Locataires" },
        ]}
      />
      <Card title={q.data?.title ?? "Notice"}>
        {q.isLoading ? <Loading /> : null}
        <ErrorBox error={q.error} />
        {q.data ? <NoticeView notice={q.data} /> : null}
        <p className="small">
          Lien public à communiquer : <span className="mono">{link}</span>
        </p>
      </Card>
    </div>
  );
}

const CATEGORY_LABELS: Record<string, string> = {
  candidature_non_retenue: "Candidatures non retenues",
  candidature_retiree: "Candidatures retirées",
  candidature_inachevee: "Candidatures non déposées",
  bail_termine: "Baux terminés",
  comptabilite_loyers: "Comptabilité des loyers",
  journal_acces: "Journal des accès",
  leads_assurance: "Demandes de mise en relation assurance",
  litige: "Gel pour litige (durée maximale)",
};
const ACTION_LABELS: Record<string, string> = { anonymiser: "Anonymiser", effacer: "Effacer", archiver: "Archiver puis revoir", revoir: "Revoir" };

function RetentionTab() {
  const org = useOrg();
  const toast = useToast();
  const invalidate = useInvalidate();
  const rules = useGet<any>(org.api("/retention/rules"));
  const runs = useGet<any>(org.api("/retention/runs"));
  const action = useAction();
  const [edit, setEdit] = useState<{ category: string; duration_days: string; justification: string } | null>(null);
  const [preview, setPreview] = useState<any>(null);
  const owner = org.role === "proprietaire";
  const doPreview = async () => {
    const r = await action.run(() => post<any>(org.api("/retention/runs"), { dry_run: true }));
    if (r) setPreview(r);
  };
  const execute = async () => {
    const r = await action.run(() => post<any>(org.api("/retention/runs"), { dry_run: false }));
    if (r) {
      toast(`${r.summary.candidatures_anonymisees} candidature(s) anonymisée(s)${r.summary.toutes_verifiees ? ", vérification réussie" : ""}.`);
      setPreview(null);
      invalidate(org.api("/retention"));
    }
  };
  const saveRule = async () => {
    if (!edit) return;
    const r = await action.run(() => put(org.api(`/retention/rules/${edit.category}`), { duration_days: parseInt(edit.duration_days, 10) || 0, justification: edit.justification }));
    if (r) {
      setEdit(null);
      invalidate(org.api("/retention/rules"));
    }
  };
  if (rules.isLoading) return <Loading />;
  if (rules.error) return <ErrorBox error={rules.error} />;
  return (
    <div className="stack">
      <Notice tone="attention">{rules.data.note}</Notice>
      <Card title="Règles de conservation">
        <Table caption="Règles de conservation">
          <thead>
            <tr>
              <th>Catégorie</th>
              <th>Déclencheur</th>
              <th className="right">Durée</th>
              <th>Action</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rules.data.items.map((r: any) => (
              <tr key={r.id}>
                <td>
                  {CATEGORY_LABELS[r.category] ?? r.category}
                  <span className="block small muted">{r.justification}</span>
                  <span className="block small muted">{r.reference}</span>
                </td>
                <td className="small">{r.trigger_event}</td>
                <td className="right num">{r.duration_days} jours</td>
                <td>
                  {ACTION_LABELS[r.action] ?? r.action}
                  {r.automated ? <span className="block small muted">par la tâche ci-dessous</span> : <span className="block small muted">revue manuelle</span>}
                </td>
                <td className="right">
                  {owner ? (
                    <Button variant="discret" small onClick={() => setEdit({ category: r.category, duration_days: String(r.duration_days), justification: r.justification })}>
                      Modifier
                    </Button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>
      <Card
        title="Tâche d’anonymisation"
        actions={
          <Button variant="secondaire" small busy={action.busy} onClick={doPreview}>
            Voir ce qui serait anonymisé
          </Button>
        }
      >
        <p className="small">
          Anonymise les candidatures non retenues, retirées ou non déposées dont la durée est écoulée : pièces effacées, identité, coordonnées, valeurs lues, échanges et réponses retirés dans tous les modules. Chaque opération est vérifiée et inscrite au journal. La tâche s’exécute aussi chaque jour automatiquement ; l’exécution manuelle est réservée à un propriétaire de l’espace.
        </p>
        <ErrorBox error={action.error && !edit ? action.error : null} />
        {preview ? (
          <div className="stack-sm">
            {preview.candidatures.length === 0 ? (
              <Notice tone="succes">Aucune candidature n’a atteint sa durée de conservation.</Notice>
            ) : (
              <Table caption="Candidatures à anonymiser">
                <thead>
                  <tr>
                    <th>Campagne</th>
                    <th>Motif</th>
                    <th>Depuis le</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.candidatures.map((c: any) => (
                    <tr key={c.application_id}>
                      <td>{c.campagne}</td>
                      <td>
                        {CATEGORY_LABELS[c.categorie] ?? c.categorie} ({c.duree_jours} jours)
                      </td>
                      <td>{date(c.depuis)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
            {preview.demandes_assurance ? <p className="small">{preview.demandes_assurance} demande(s) de mise en relation assurance à anonymiser.</p> : null}
            {(preview.gelees ?? []).length ? (
              <p className="small">
                {preview.gelees.length} candidature(s) échue(s) mais gelée(s) pour litige : elles ne seront pas anonymisées avant la fin du gel.
              </p>
            ) : null}
            {(preview.revues ?? []).map((r: any) => (
              <p key={r.categorie} className="small">
                {CATEGORY_LABELS[r.categorie] ?? r.categorie} : {r.elements} élément(s) au-delà de {r.duree_jours} jours, à revoir (une tâche est préparée ; rien n’est supprimé automatiquement).
              </p>
            ))}
            {owner && (preview.candidatures.length || preview.demandes_assurance) ? (
              <p>
                <Button variant="danger" busy={action.busy} onClick={execute}>
                  Anonymiser maintenant
                </Button>
              </p>
            ) : null}
          </div>
        ) : null}
        {(runs.data?.items ?? []).length ? (
          <details>
            <summary className="small">Exécutions précédentes</summary>
            <ul className="list-plain small">
              {runs.data.items.map((r: any) => (
                <li key={r.id}>
                  {dateTime(r.started_at)} · {r.started_by_name ?? "—"} · {r.summary.candidatures_anonymisees ?? 0} candidature(s), {r.summary.demandes_assurance_anonymisees ?? 0} demande(s) ·{" "}
                  {r.summary.toutes_verifiees ? "vérifiées" : "vérification incomplète"}
                </li>
              ))}
            </ul>
          </details>
        ) : null}
      </Card>
      <HoldsCard />
      {(rules.data.platform_rules ?? []).length ? (
        <Card title="Règles de la plateforme">
          <p className="small muted">Données tenues par Bailivo pour l’ensemble des espaces : durées fixes, appliquées par la tâche quotidienne.</p>
          <Table caption="Règles de la plateforme">
            <thead>
              <tr>
                <th>Finalité</th>
                <th>Déclencheur</th>
                <th className="right">Durée</th>
                <th>Données retirées</th>
              </tr>
            </thead>
            <tbody>
              {rules.data.platform_rules.map((r: any) => (
                <tr key={r.code}>
                  <td>
                    {r.purpose}
                    <span className="block small muted">{r.justification}</span>
                    <span className="block small muted">{r.reference}</span>
                  </td>
                  <td className="small">{r.trigger}</td>
                  <td className="right num">{r.duration_days} jours</td>
                  <td className="small">{r.neutralized}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      ) : null}
      {(rules.data.exceptions ?? []).length ? (
        <Card title="Ce qui n’est pas effacé, et jusqu’à quand">
          <ul className="list-plain stack-sm">
            {rules.data.exceptions.map((e: any) => (
              <li key={e.code}>
                <strong>{e.label}</strong>
                <span className="block small">{e.kept}</span>
                <span className="block small muted">Pourquoi : {e.justification}</span>
                <span className="block small muted">Jusqu’à : {e.bound}</span>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}
      <Modal
        open={!!edit}
        onClose={() => setEdit(null)}
        title={edit ? `Durée : ${CATEGORY_LABELS[edit.category] ?? edit.category}` : ""}
        footer={
          <>
            <Button variant="discret" onClick={() => setEdit(null)}>
              Annuler
            </Button>
            <Button busy={action.busy} onClick={saveRule}>
              Enregistrer
            </Button>
          </>
        }
      >
        {edit ? (
          <div className="stack-sm">
            <Input label="Durée en jours" inputMode="numeric" value={edit.duration_days} error={fieldError(action.error, "duration_days")} onChange={(e) => setEdit({ ...edit, duration_days: e.target.value })} />
            <Textarea label="Justification" value={edit.justification} error={fieldError(action.error, "justification")} onChange={(e) => setEdit({ ...edit, justification: e.target.value })} />
          </div>
        ) : null}
      </Modal>
    </div>
  );
}

const SUBJECT_LABELS: Record<string, string> = {
  candidature: "Candidature",
  document: "Pièce",
  compte: "Compte",
  lead_assurance: "Demande assurance",
  demande_droits: "Demande d’exercice des droits",
  signalement: "Signalement d’annonce",
};
const ACTION_DONE: Record<string, string> = { anonymise: "anonymisé(e)", efface: "effacé(e)", conserve: "conservé(e) : gel pour litige" };

function ErasuresTab() {
  const org = useOrg();
  const q = useGet<any>(org.api("/retention/erasures"));
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  if (!q.data.items.length) return <Empty title="Aucun effacement enregistré.">Les anonymisations et effacements apparaîtront ici avec leur vérification.</Empty>;
  return (
    <Card title="Journal des effacements">
      <Table caption="Effacements">
        <thead>
          <tr>
            <th>Date</th>
            <th>Élément</th>
            <th>Motif</th>
            <th>Vérification</th>
          </tr>
        </thead>
        <tbody>
          {q.data.items.map((e: any) => (
            <tr key={e.id}>
              <td>
                {dateTime(e.performed_at)}
                <span className="block small muted">{e.performed_by_name ?? "Tâche"}</span>
              </td>
              <td>
                {SUBJECT_LABELS[e.subject_type] ?? e.subject_type} {ACTION_DONE[e.action] ?? e.action}
                <span className="block small muted mono">{e.subject_id.slice(0, 8)}</span>
              </td>
              <td className="small">
                {e.reason}
                <span className="block muted">
                  {Object.entries(e.counts)
                    .filter(([, v]) => typeof v !== "object")
                    .map(([k, v]) => `${k.replace(/_/g, " ")} : ${v}`)
                    .join(" · ")}
                </span>
                {e.counts?.modules ? (
                  <span className="block muted">
                    Modules :{" "}
                    {Object.entries(e.counts.modules as Record<string, Record<string, number>>)
                      .map(([m, c]) => `${m} (${Object.values(c).reduce((a, b) => a + Number(b || 0), 0)} élément(s))`)
                      .join(", ")}
                  </span>
                ) : null}
              </td>
              <td>{e.verified ? <Badge tone="succes">Vérifié</Badge> : <Badge tone="erreur">À contrôler</Badge>}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Card>
  );
}

const ACCESS_LABELS: Record<string, string> = {
  "document.consulte": "Consultation",
  "document.depose": "Dépôt",
  "document.efface": "Effacement",
  "document.copie_filigranee": "Copie filigranée",
};

function AccessTab() {
  const org = useOrg();
  const q = useGet<any>(org.api("/document-access"));
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  if (!q.data.items.length) return <Empty title="Aucun accès enregistré." />;
  return (
    <Card title="Accès aux pièces">
      <p className="small muted">Chaque consultation, dépôt, copie ou effacement d’une pièce est journalisé.</p>
      <Table caption="Accès aux pièces">
        <thead>
          <tr>
            <th>Date</th>
            <th>Personne</th>
            <th>Action</th>
            <th>Pièce</th>
          </tr>
        </thead>
        <tbody>
          {q.data.items.map((a: any) => (
            <tr key={a.id}>
              <td>{dateTime(a.created_at)}</td>
              <td>{a.user_name ?? "—"}</td>
              <td>
                {ACCESS_LABELS[a.action] ?? a.action}
                {a.details?.finalite ? <span className="block small muted">{a.details.finalite}</span> : null}
              </td>
              <td>
                {a.document_title ?? "—"}
                {a.category ? <span className="block small muted">{a.category}</span> : null}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Card>
  );
}

/** Page publique : notice d'information d'un espace (lien communiqué aux candidats et locataires). */
export function PublicNoticePage() {
  const { orgId = "", audience = "" } = useParams();
  const q = useGet<any>(`/api/v1/public/privacy-notices/${orgId}/${audience}`);
  return (
    <PublicShell wide>
      {q.isLoading ? <Loading /> : null}
      <ErrorBox error={q.error} />
      {q.data ? (
        <div className="stack">
          <h1>{q.data.title}</h1>
          <NoticeView notice={q.data} />
        </div>
      ) : null}
    </PublicShell>
  );
}

/** Section candidat : notice de l'espace et accusé de lecture. */
export function CandidateNoticeSection({ applicationId }: { applicationId: string }) {
  return <NoticeSection path={`/api/v1/applications/${applicationId}/privacy-notice`} audience="candidat" />;
}

export function TenantNoticeSection({ leaseId }: { leaseId: string }) {
  return <NoticeSection path={`/api/v1/leases/${leaseId}/privacy-notice`} audience="locataire" />;
}

function NoticeSection({ path, audience }: { path: string; audience: string }) {
  const q = useGet<any>(path);
  const invalidate = useInvalidate();
  const action = useAction();
  if (!q.data) return q.error ? <ErrorBox error={q.error} /> : <Loading />;
  const ack = async () => {
    const r = await action.run(() => post("/api/v1/consents", { org_id: q.data.org_id, audience }));
    if (r) invalidate(path);
  };
  return (
    <Card title="Vos données personnelles">
      <NoticeView notice={q.data.notice} />
      <ErrorBox error={action.error} />
      {q.data.acknowledged_at ? (
        <p className="small muted">Lue le {dateTime(q.data.acknowledged_at)}.</p>
      ) : (
        <Button variant="secondaire" small busy={action.busy} onClick={ack}>
          J’ai pris connaissance de cette information
        </Button>
      )}
      <p className="small">
        Pour exporter vos données ou demander leur effacement : <Link to="/moi/donnees">Mes données</Link>.
      </p>
    </Card>
  );
}
